import { createSession, submitOnramp } from "../api/client";
import type { QuickFacts } from "../api/types";

export interface Stage {
  mount(container: HTMLElement): void;
  unmount(): void;
}

/* SVG icons — thin outline style (Lucide-inspired), 20×20 */
const ICONS = {
  search: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  target: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>`,
  database: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>`,
  bar_chart: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>`,
};

const INTENTS = [
  {
    icon: ICONS.search,
    title: "Find where AI can help us",
    desc: "I know we should be doing something with AI, but I don't know where to start.",
    value: "I know we should be doing something with AI",
  },
  {
    icon: ICONS.target,
    title: "Evaluate specific problems",
    desc: "I have pain points in mind — tell me whether AI is the right tool for them.",
    value: "We have specific problems",
  },
  {
    icon: ICONS.database,
    title: "Check our data readiness",
    desc: "We want to understand what we'd need to put in place before starting any AI project.",
    value: "Check our data readiness",
  },
  {
    icon: ICONS.bar_chart,
    title: "Benchmark against our industry",
    desc: "Show me what companies like ours are already doing with AI today.",
    value: "Benchmark against our industry",
  },
];

export class Stage0Onramp implements Stage {
  private onComplete: (sessionId: string) => void;
  private selectedChips: Map<string, string> = new Map();
  private selectedIntent: string | null = null;

  constructor(onComplete: (sessionId: string) => void) {
    this.onComplete = onComplete;
  }

  mount(container: HTMLElement): void {
    container.innerHTML = `
      <div class="onramp">
        <h1>Find out where AI can help your business.</h1>
        <p class="lede">A 10-minute guided session. No slideware, no jargon. You'll walk away with a concrete blueprint of where AI fits — and where it doesn't — for your specific company.</p>

        <div class="tile-section-label">What brings you here today?</div>
        <div class="tiles" role="group" aria-label="Select your primary intent">
          ${INTENTS.map((t) => `
            <div class="tile" data-intent="${t.value}" role="button" tabindex="0" aria-pressed="false">
              <div class="tile-icon" aria-hidden="true">${t.icon}</div>
              <h3>${t.title}</h3>
              <p>${t.desc}</p>
            </div>
          `).join("")}
        </div>

        <div class="quick-facts">
          <div class="quick-facts-header">
            <h3>A few quick facts about your company</h3>
            <span>~30 seconds</span>
          </div>
          ${this._chipRow("Company size", "company_size", ["1–10", "11–50", "51–250", "250+"])}
          ${this._chipRow("Industry", "industry", ["Manufacturing", "Services", "Retail", "Healthcare", "Other"])}
          ${this._chipRow("Current AI use", "current_ai_use", ["None yet", "Experimenting", "Some in production"])}
          ${this._chipRow("Main systems", "main_systems", ["Microsoft 365", "SAP", "Salesforce", "Google Workspace"])}
        </div>

        <button class="btn primary start-btn" style="margin-top:var(--space-lg);width:100%;justify-content:center;font-size:14px;padding:12px 20px;" disabled>
          Start session
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
      </div>
    `;

    this._bindChips(container);
    this._bindTiles(container);
  }

  unmount(): void {}

  private _chipRow(label: string, key: string, options: string[]): string {
    return `
      <div class="qf-row">
        <div class="qf-label">${label}</div>
        <div class="qf-chips" role="group" aria-label="${label}">
          ${options.map((o) => `<button class="chip" data-key="${key}" data-value="${o}" type="button">${o}</button>`).join("")}
        </div>
      </div>
    `;
  }

  private _bindChips(container: HTMLElement): void {
    container.querySelectorAll<HTMLButtonElement>(".qf-chips .chip").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset["key"]!;
        const value = btn.dataset["value"]!;

        if (key === "main_systems") {
          btn.classList.toggle("selected");
          btn.setAttribute("aria-pressed", btn.classList.contains("selected").toString());
        } else {
          container.querySelectorAll<HTMLButtonElement>(`.chip[data-key="${key}"]`).forEach((b) => {
            b.classList.remove("selected");
            b.setAttribute("aria-pressed", "false");
          });
          btn.classList.add("selected");
          btn.setAttribute("aria-pressed", "true");
          this.selectedChips.set(key, value);
        }

        this._updateStartButton(container);
      });
    });
  }

  private _bindTiles(container: HTMLElement): void {
    container.querySelectorAll<HTMLElement>(".tile").forEach((tile) => {
      const activate = () => {
        container.querySelectorAll<HTMLElement>(".tile").forEach((t) => {
          t.classList.remove("selected");
          t.setAttribute("aria-pressed", "false");
        });
        tile.classList.add("selected");
        tile.setAttribute("aria-pressed", "true");
        this.selectedIntent = tile.dataset["intent"] ?? null;
        this._updateStartButton(container);
      };

      tile.addEventListener("click", activate);
      tile.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); }
      });
    });

    const btn = container.querySelector<HTMLButtonElement>(".start-btn")!;
    btn.addEventListener("click", () => this._tryStart(container));
  }

  private _updateStartButton(container: HTMLElement): void {
    const btn = container.querySelector<HTMLButtonElement>(".start-btn");
    if (btn) btn.disabled = !this.selectedIntent;
  }

  private async _tryStart(container: HTMLElement): Promise<void> {
    if (!this.selectedIntent) return;

    const btn = container.querySelector<HTMLButtonElement>(".start-btn")!;
    btn.disabled = true;
    btn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="animation:spin 1s linear infinite"><circle cx="12" cy="12" r="10" stroke-dasharray="40" stroke-dashoffset="10"/></svg>
      Starting...
    `;

    const mainSystems = Array.from(
      container.querySelectorAll<HTMLButtonElement>('.chip[data-key="main_systems"].selected'),
    ).map((b) => b.dataset["value"]!);

    const facts: Partial<QuickFacts> = {
      company_size: this.selectedChips.get("company_size") ?? null,
      industry: this.selectedChips.get("industry") ?? null,
      current_ai_use: this.selectedChips.get("current_ai_use") ?? null,
      main_systems: mainSystems,
      entry_intent: this.selectedIntent,
    };

    try {
      const { session_id } = await createSession();
      localStorage.setItem("session_id", session_id);
      await submitOnramp(session_id, facts);
      this.onComplete(session_id);
    } catch {
      btn.disabled = false;
      btn.innerHTML = `Start session <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`;
      const errEl = container.querySelector(".start-error");
      if (!errEl) {
        btn.insertAdjacentHTML("afterend", `<p class="start-error" style="color:var(--swo-red);font-size:13px;margin-top:var(--space-sm);text-align:center;">Failed to start session. Please try again.</p>`);
      }
    }
  }
}
